/**
 * Undo/redo of the journey editor (docs/pilot/SPEC.md §12.4): a stack of immutable snapshots, at most 100. One action
 * is one entry; a drag is one entry on release; typing into one field of the step panel within 1 s is glued into one
 * entry (same `coalesce` key). Undo/redo never touch the server — autosave saves whatever is current. Pure.
 */

export const HISTORY_LIMIT = 100;
export const COALESCE_MS = 1000;

export interface History<T> {
  past: T[];
  present: T;
  future: T[];
  /** Key and time of the last push, for gluing edits of one field. */
  lastKey: string | null;
  lastAt: number;
}

export const historyOf = <T,>(present: T): History<T> => ({ past: [], present, future: [], lastKey: null, lastAt: 0 });

export function push<T>(h: History<T>, next: T, opts: { coalesce?: string; now?: number; windowMs?: number } = {}): History<T> {
  const now = opts.now ?? Date.now();
  const glue = !!opts.coalesce && opts.coalesce === h.lastKey && now - h.lastAt <= (opts.windowMs ?? COALESCE_MS) && h.past.length > 0;
  if (glue) return { ...h, present: next, future: [], lastAt: now };
  const past = [...h.past, h.present];
  if (past.length > HISTORY_LIMIT) past.splice(0, past.length - HISTORY_LIMIT);
  return { past, present: next, future: [], lastKey: opts.coalesce ?? null, lastAt: now };
}

export const canUndo = <T,>(h: History<T>) => h.past.length > 0;
export const canRedo = <T,>(h: History<T>) => h.future.length > 0;

export function undo<T>(h: History<T>): History<T> {
  if (!h.past.length) return h;
  const prev = h.past[h.past.length - 1];
  return { past: h.past.slice(0, -1), present: prev, future: [h.present, ...h.future], lastKey: null, lastAt: 0 };
}

export function redo<T>(h: History<T>): History<T> {
  if (!h.future.length) return h;
  const [next, ...rest] = h.future;
  return { past: [...h.past, h.present], present: next, future: rest, lastKey: null, lastAt: 0 };
}

/** Replaces the present without an undo entry (e.g. positions placed automatically for new steps). */
export const replacePresent = <T,>(h: History<T>, present: T): History<T> => ({ ...h, present });
